import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
    { label: 'About', to: '/about', testId: 'nav-about' },
    { label: 'Agritourism', to: '/verticals/agritourism', testId: 'nav-agritourism' },
    { label: 'Gatherings', to: '/verticals/gatherings', testId: 'nav-gatherings' },
    { label: 'Volunteer', to: '/verticals/volunteering', testId: 'nav-volunteering' },
    { label: 'Campaigns', to: '/verticals/campaigns', testId: 'nav-campaigns' },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? 'bg-[#F9F6F0]/85 backdrop-blur-md border-b border-aurika-border'
                    : 'bg-transparent'
            }`}
        >
            <div className="container-editorial flex items-center justify-between py-5">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
                    <span className="font-display text-2xl md:text-[26px] leading-none text-aurika-obsidian tracking-tight">
                        Aurika
                    </span>
                    <span className="hidden md:inline overline text-[0.6rem] text-aurika-muted">
                        Collective Foundation
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.testId}
                            className={({ isActive }) =>
                                `text-[13px] tracking-wide transition-colors ${
                                    isActive ? 'text-aurika-terracotta' : 'text-aurika-obsidian hover:text-aurika-terracotta'
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/partner"
                        data-testid="nav-partner-cta"
                        className="hidden md:inline-flex btn-primary !py-3 !px-5 text-[11px]"
                    >
                        Partner With Us
                    </Link>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden p-2 -mr-2 text-aurika-obsidian"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-panel"
                    className="lg:hidden bg-[#F9F6F0] border-t border-aurika-border"
                >
                    <div className="container-editorial py-6 flex flex-col gap-5">
                        {NAV_LINKS.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`${l.testId}-mobile`}
                                className="text-lg font-display text-aurika-obsidian"
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link
                            to="/partner"
                            data-testid="nav-partner-cta-mobile"
                            className="btn-primary self-start mt-2"
                        >
                            Partner With Us
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
